package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.RankedOption
import de.xtl.retroguide.protocol.VisibleAnswer
import de.xtl.retroguide.protocol.VotingOption
import de.xtl.retroguide.question.accentColor
import de.xtl.retroguide.question.label
import de.xtl.retroguide.votingRanking
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.Res
import retroguide.app.shared.generated.resources.dot_vote_members
import retroguide.app.shared.generated.resources.dot_vote_point_single
import retroguide.app.shared.generated.resources.dot_vote_points
import retroguide.app.shared.generated.resources.voting_ranking_title

private val Medals = listOf("🥇", "🥈", "🥉")
private val PodiumColors = listOf(Color(0xFFF9A825), Color(0xFF90A4AE), Color(0xFFBF7B45))

/** Options ranked by points; bars grow one after another, the top three get medals. */
@Composable
internal fun VotingRanking(options: List<VotingOption>, votes: List<VisibleAnswer>) {
    if (options.isEmpty()) return
    val ranking = votingRanking(options, votes)
    val maxPoints = ranking.maxOf { it.points }.coerceAtLeast(1)
    ElevatedCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(stringResource(Res.string.voting_ranking_title), style = MaterialTheme.typography.titleMedium)
            ranking.forEachIndexed { index, ranked ->
                key(ranked.option.id) { RankingRow(index, ranked, maxPoints) }
            }
        }
    }
}

@Composable
private fun RankingRow(index: Int, ranked: RankedOption, maxPoints: Int) {
    val fill = remember { Animatable(0f) }
    val target = ranked.points.toFloat() / maxPoints
    LaunchedEffect(target) { fill.animateTo(target, tween(700, delayMillis = index * 120, easing = FastOutSlowInEasing)) }
    val podium = ranked.points > 0 && index < Medals.size
    val barColor = if (podium) PodiumColors[index] else MaterialTheme.colorScheme.primary.copy(alpha = .55f)
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        Text(
            if (podium) Medals[index] else "${index + 1}.",
            modifier = Modifier.width(32.dp),
            style = MaterialTheme.typography.titleLarge,
            textAlign = TextAlign.Center,
        )
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    ranked.option.label,
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = if (podium) FontWeight.SemiBold else FontWeight.Normal,
                )
                Text(
                    if (ranked.points == 1) {
                        stringResource(Res.string.dot_vote_point_single)
                    } else {
                        stringResource(Res.string.dot_vote_points, ranked.points)
                    },
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            ranked.option.category?.let { category ->
                Text("${category.emoji} ${category.label()}", style = MaterialTheme.typography.labelMedium, color = category.accentColor)
            } ?: ranked.option.memberCount.takeIf { it > 1 }?.let {
                Text(
                    stringResource(Res.string.dot_vote_members, it),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            Box(
                Modifier
                    .fillMaxWidth()
                    .height(10.dp)
                    .clip(RoundedCornerShape(5.dp))
                    .background(MaterialTheme.colorScheme.surfaceVariant),
            ) {
                Box(
                    Modifier
                        .fillMaxHeight()
                        .fillMaxWidth(fill.value.coerceIn(0f, 1f))
                        .background(barColor, RoundedCornerShape(5.dp)),
                )
            }
        }
    }
}
